import * as THREE from "three";
import { Cell, CellKind, LevelSim, Occupant, SimEventKind, wildPetSkin } from "@highmarch/domain";
import { GroundMesh } from "../groundMesh";
import type { PooledVfx } from "../vfxPool";
import { EnemyViews } from "./enemies";
import { PetBody } from "./petBody";
import { PET_MOVE, clipRate, turnToward } from "./petGait";
import { PetHop } from "./petHop";
import { PetMarker } from "./petMarker";
import { keepHides, towerHides } from "./petMind";
import { petLook } from "./petPresent";
import { lib } from "./common";
import { SkinKits } from "../skins/kit/runtime";
import { Romp, Spot, clearRun, jittered, leaveSpot, nextRomp, rompCells } from "./wildPetPlan";
import { WILD_LOOK, caughtBurst, goneSparkle } from "./wildPetLook";

type Phase = "none" | "romp" | "leave" | "dash" | "chase" | "caught" | "escape";
const _v = new THREE.Vector3();

/**
 * The wild pet (Pets v2, docs/PETS.md), presentation only. The sim says when it is out (`sim.wildPet.phase`: visible,
 * left, engaged, caught, escaped); this draws it. Visible: it romps on the open grass by the middle of the road
 * (wildPetPlan.ts: dashes, the zoomies, play-bows, rolls, sniffs) in the coat it rolled (`wildPetSkin`), with a pulsing
 * gold ring under it — and a glint if it wears a rare coat — so the player finds it; `hit` answers the level's tap.
 * Left: it trots off the map. Engaged: it dashes for the portal and vanishes in a puff, and comes down the road as the
 * boss — the raider's own body hidden (EnemyViews.setBodyHidden, as Polymorph's sheep), this pet drawn where that body
 * is, charging (`boss_run` at its measured stride), biting the troops that block it. Caught: the `caught` clip where it
 * fell, hearts and sparkles. Escaped: it runs on past the gate and is gone.
 */
export class WildPetView {
  readonly group = new THREE.Group();
  private body: PetBody | null = null;
  private marker: PetMarker | null = null;
  private readonly hop = new PetHop();
  private phase: Phase = "none";
  private seen = "";
  private skin: string | null = null;
  private cells: Spot[] = [];
  /** Open grass it may stand or run on (set when it appears): no canyon, cliff, rock, tower or road. */
  private open: (x: number, y: number) => boolean = () => false;
  private romp: Romp | null = null;
  private rompLeft = 0;
  private yaw = 0;
  private t = 0;
  private pulseIn = 0;
  private glintIn = 0;
  private enemyId = 0;
  private readonly last = new THREE.Vector3();
  private hasLast = false;
  private bitIn = -1;
  /** The sprite VFX, once loaded (level/levelScene.ts attachRouter). */
  fx: (() => PooledVfx | null) | null = null;
  /** Hides / shows a raider's own body (EnemyViews.setBodyHidden) and lifts its plate to the pet's head. */
  hideEnemy: (id: number, hidden: boolean, headY: number) => void = () => {};
  /** Where a raider is drawn now (EnemyViews.feet), or null. */
  enemyAt: (id: number) => THREE.Vector3 | null = () => null;

  /** `toCamera`: the unit ground direction toward the camera, so it romps where it is seen (never behind a tower or the keep). */
  constructor(private readonly sim: LevelSim, private readonly toCamera: (() => { readonly x: number; readonly z: number }) | null = null, private readonly rand: () => number = Math.random) {
    // the run's wild pet and the coat it rolled are known from the start: its models load long before it appears
    const w = sim.wildPet, L = lib();
    if (w && L) void L.ensure(SkinKits.petAssetNames(w.petId, wildPetSkin(sim)).filter((n) => L.ships(n)));
  }

  /** The skin the wild pet wears now (a catalogue id), or null. */
  get coat(): string | null { return this.skin; }
  /** Whether it is out on the grass to be tapped. */
  get tappable(): boolean { return this.phase === "romp" && this.body !== null; }

  /** Per sim tick: its bites on the troops that block it (EnemyBlocked by its raider). */
  onTick(): void {
    if (this.phase !== "chase") return;
    for (const ev of this.sim.events.all()) if (ev.kind === SimEventKind.EnemyBlocked && ev.a === this.enemyId && this.bitIn < 0) this.bitIn = 0;
  }

  /** Whether the screen point (x, y) CSS px is on the romping pet (`project`: world → CSS px). */
  hit(x: number, y: number, project: (x: number, y: number, z: number) => { x: number; y: number }): boolean {
    const b = this.body;
    if (!this.tappable || !b) return false;
    const p = b.root.position, r = WILD_LOOK.reachPx;
    for (const h of [0, 0.12 * b.scale]) { const s = project(p.x, p.y + h, p.z); if ((s.x - x) ** 2 + (s.y - y) ** 2 <= r * r) return true; }
    return false;
  }

  /** Where it stands now (world), or null when it is not drawn. */
  get at(): THREE.Vector3 | null { return this.body ? this.body.root.position : null; }

  interpolate(dt: number): void {
    this.t += dt;
    const w = this.sim.wildPet, key = w ? `${w.phase}:${w.enemyId}` : "";
    if (key !== this.seen) { this.seen = key; this.enter(w?.phase ?? "pending"); }
    switch (this.phase) {
      case "romp": this.frameRomp(dt); break;
      case "leave": this.frameMove(dt, () => this.poof()); break;
      case "dash": if ((this.rompLeft -= dt) <= 0) this.vanish(); else this.frameMove(dt, () => this.vanish()); break;
      case "chase": this.frameChase(dt); break;
      case "caught": this.frameCaught(dt); break;
      case "escape": this.frameMove(dt, () => this.poof()); break;
      default: break;
    }
    this.marker?.update(dt, this.hop.x, this.groundAt(this.hop.x, this.hop.z), this.hop.z);
  }

  private enter(phase: string): void {
    const w = this.sim.wildPet;
    if (!w) return;
    if (phase === "visible") { this.appear(w.petId); return; }
    if (phase === "left" && this.body) {
      // untapped: it scampers off over open grass and vanishes in a puff (never off the map's edge, a cliff)
      this.phase = "leave"; this.endRomp(); this.dropMarker();
      const to = leaveSpot(this.hop, this.cells, this.open);
      if (to) this.runTo(jittered(to, 0.5, 0.5)); else this.poof();
      return;
    }
    if (phase === "engaged") {
      this.enemyId = w.enemyId;
      this.dropMarker();
      if (this.body) {
        // the tap: it bolts toward the portal and vanishes in a puff; the boss pet comes down the road from there
        const p = this.sim.road.portal;
        this.phase = "dash"; this.rompLeft = WILD_LOOK.dashSeconds; this.endRomp();
        const to = { x: this.hop.x + Math.sign(p.x - this.hop.x) * 1.2, z: this.hop.z + Math.sign(p.y - this.hop.z) * 1.2 };
        if (clearRun(this.hop, to, this.open)) this.runTo(to); else this.rompLeft = 0.01;   // nothing open that way: it goes in its puff at once
      } else this.startChase();
      return;
    }
    if (phase === "caught") { this.caught(); return; }
    if (phase === "escaped") { this.escape(); return; }
  }

  /** It appears on the grass: a spot by the middle of the road, the coat it rolled, the pulse ring under it. */
  private appear(petId: string): void {
    const g = this.sim.grid, road = this.sim.road;
    const towers = this.sim.towerSlots.filter((t) => t.alive).map((t) => t.cell), keep = g.footprint, cam = this.toCamera?.() ?? null;
    const free = (x: number, y: number) => { const c = new Cell(x, y); return g.kindAt(c) === CellKind.Empty && road.occupantAt(c) === Occupant.None && !towers.some((t) => t.x === x && t.y === y); };
    const seen = (x: number, y: number) => cam === null || (!towerHides(x, y, towers, cam) && !keepHides(x, y, keep, cam));
    // open grass it can be seen on; all the open grass when the shot hides every cell of it
    const cells = rompCells(road.roadCells, g.width, g.height, (x, y) => free(x, y) && seen(x, y));
    this.cells = cells.length > 0 ? cells : rompCells(road.roadCells, g.width, g.height, free);
    this.open = (x, y) => x >= 0 && y >= 0 && x < g.width && y < g.height && free(x, y);
    if (this.cells.length === 0) return;
    this.skin = wildPetSkin(this.sim);
    this.build(petId, petLook().scale);
    const at = jittered(this.cells[Math.floor(this.rand() * this.cells.length)], this.rand(), this.rand());
    this.hop.place(at.x, at.z);
    this.yaw = this.rand() * Math.PI * 2;
    this.marker = new PetMarker(petLook().scale);
    this.group.add(this.marker.mesh);
    this.phase = "romp"; this.romp = null; this.rompLeft = 0; this.pulseIn = 0;
    // a puff of dust as it bounds in
    this.fx?.()?.dust(_v.set(at.x, this.groundAt(at.x, at.z) + 0.05, at.z), 8, { size: 0.3, speed: 1, life: 0.6 });
  }

  private build(petId: string, scale: number): void {
    this.body?.dispose();
    const b = new PetBody(petId, scale, this.skin, EnemyViews.lodFar());     // out at the far zoom: its LOD, as the grunts
    b.fx = () => this.fx?.() ?? null;
    this.body = b;
    this.hop.configure(b.gaits, scale);
    this.group.add(b.root, b.shadow);
  }

  private frameRomp(dt: number): void {
    const b = this.body;
    if (!b) return;
    // the pulse that makes it findable, and a coat's glint
    if ((this.pulseIn -= dt) <= 0) { this.pulseIn = WILD_LOOK.pulseEvery; this.marker?.flare(); }
    if (this.skin && (this.glintIn -= dt) <= 0) {
      this.glintIn = WILD_LOOK.glintEvery;
      const p = b.root.position;
      this.fx?.()?.sparkles(_v.set(p.x, p.y + 0.15 * b.scale, p.z), 3, { color: WILD_LOOK.glint, radius: 0.22 * b.scale, rise: 0.5, life: 0.6, size: 0.1 });
    }
    if (this.romp === null || (this.romp.kind === "dash" ? !this.hop.moving : (this.rompLeft -= dt) <= 0)) {
      this.endRomp();
      const r = nextRomp(this.hop, this.cells, this.romp?.kind ?? null, this.rand, this.open);
      this.romp = r;
      if (r.kind === "dash") this.runTo(r.to);
      else { this.rompLeft = r.seconds; if (r.kind !== "zoomies") b.play(r.kind === "sniff" ? "fidget" : r.kind); }
    }
    this.hop.step(dt);
    if (this.hop.moving) this.yaw = turnToward(this.yaw, this.hop.heading, PET_MOVE.turnRate, dt);
    const zoom = this.romp?.kind === "zoomies";
    if (zoom) this.yaw += dt * Math.PI * 1.6;                 // the zoomies spin it round on the spot
    const gait = this.hop.gait;
    const loop = gait && this.hop.setOff && this.hop.left > PET_MOVE.fade / 2 ? gait : zoom ? "zoomies" : "idle";
    this.pose(dt, loop, this.hop.rate);
  }

  private endRomp(): void { if (this.romp && this.romp.kind !== "dash" && this.romp.kind !== "zoomies") this.body?.endAct(); }

  private runTo(to: Spot): void { this.hop.go(to.x, to.z, [], this.yaw); }

  /** A plain move (leaving, the dash, the escape): runs there, then `done`. */
  private frameMove(dt: number, done: () => void): void {
    if (!this.body) return;
    this.hop.step(dt);
    if (this.hop.moving) this.yaw = turnToward(this.yaw, this.hop.heading, PET_MOVE.turnRate, dt);
    this.pose(dt, this.hop.moving ? this.hop.gait ?? "run" : "idle", this.hop.rate);
    if (!this.hop.moving) done();
  }

  /** It left untapped: it goes in a puff of dust where it stopped. */
  private poof(): void {
    const b = this.body;
    if (b) { const p = b.root.position; this.fx?.()?.dust(_v.set(p.x, p.y + 0.05, p.z), 12, { size: 0.35, speed: 1.4, life: 0.6 }); }
    this.clear();
  }

  /** The field pet goes in a puff; the boss pet takes over at the portal. */
  private vanish(): void {
    const b = this.body;
    if (b) { const p = b.root.position; this.fx?.()?.dust(_v.set(p.x, p.y + 0.05, p.z), 12, { size: 0.35, speed: 1.4, life: 0.6 }); }
    this.startChase();
  }

  private startChase(): void {
    const w = this.sim.wildPet;
    if (!w) return;
    this.phase = "chase";
    this.build(w.petId, petLook().scale * WILD_LOOK.bossScale);
    const b = this.body!;
    this.hideEnemy(this.enemyId, true, 0.2 * b.scale);
    this.hasLast = false;
    const p = this.sim.road.portal;
    this.hop.place(p.x, p.y);
    b.root.visible = false;                               // until its raider is drawn
  }

  private frameChase(dt: number): void {
    const b = this.body, at = this.enemyAt(this.enemyId);
    if (!b) return;
    if (!at) { b.visible = false; return; }
    b.visible = true;
    const speed = this.hasLast && dt > 0 ? Math.hypot(at.x - this.last.x, at.z - this.last.z) / dt : 0;
    if (this.hasLast && speed > 0.05) this.yaw = turnToward(this.yaw, Math.atan2(at.x - this.last.x, at.z - this.last.z), PET_MOVE.turnRate * 1.5, dt);
    this.last.copy(at); this.hasLast = true;
    this.hop.place(at.x, at.z);
    if (this.bitIn === 0) { b.play("bite"); this.bitIn = -1; }
    const [lo, hi] = WILD_LOOK.bossRate;
    const rate = Math.min(hi, Math.max(lo, speed > 0.05 ? clipRate(speed, b.bossGait, b.scale) : lo));
    this.pose(dt, speed > 0.05 ? "boss_run" : "idle", rate);
  }

  /** Beaten: it sits up dizzy where it fell (the `caught` clip, held), hearts and sparkles burst. */
  private caught(): void {
    const b = this.body;
    if (!b) return;
    this.phase = "caught"; this.rompLeft = WILD_LOOK.caughtSeconds;
    const at = this.enemyAt(this.enemyId) ?? b.root.position;
    this.hop.place(at.x, at.z);
    b.visible = true;
    b.play("caught", { hold: true });
    caughtBurst(this.fx?.() ?? null, at.x, this.groundAt(at.x, at.z), at.z, b.scale);
  }

  private frameCaught(dt: number): void {
    const b = this.body;
    if (!b) return;
    this.pose(dt, "idle", 1);
    if ((this.rompLeft -= dt) <= 0) {
      goneSparkle(this.fx?.() ?? null, b.root.position, b.scale);
      this.clear();
    }
  }

  /** It reached the gate: it bounds on past it and is gone. */
  private escape(): void {
    if (!this.body) return;
    this.phase = "escape";
    const base = this.sim.grid.base, at = this.enemyAt(this.enemyId) ?? this.body.root.position;
    this.hop.place(at.x, at.z);
    // a bound on toward the gate, then gone in a puff (never on past it, where the map may end in a cliff)
    this.runTo({ x: at.x + (base.x - at.x) * 0.5, z: at.z + (base.y - at.z) * 0.5 });
  }

  private pose(dt: number, loop: Parameters<PetBody["pose"]>[1]["loop"], rate: number): void {
    const b = this.body!;
    b.root.position.set(this.hop.x, 0, this.hop.z);
    b.pose(dt, { loop, rate, yaw: this.yaw, ground: this.groundAt(this.hop.x, this.hop.z), act: loop, actT: this.t });
  }

  private groundAt(x: number, z: number): number { return GroundMesh.drawnAt(x, z); }

  private dropMarker(): void { this.marker?.dispose(); this.marker = null; }

  private clear(): void {
    if (this.enemyId) this.hideEnemy(this.enemyId, false, -1);
    this.body?.dispose(); this.body = null;
    this.dropMarker();
    this.phase = "none";
  }

  dispose(): void { this.clear(); this.group.clear(); }
}
